import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import MarkdownRenderer from '../ai-text-generator/components/MarkdownRenderer';
import { jsPDF } from 'jspdf';
import { Document, Packer, Paragraph, TextRun, HeadingLevel } from 'docx';
import { Download } from 'lucide-react';
import GlobalNavigationMenu from '../../components/GlobalNavigationMenu';

export default function GoldenSolutionResultPage() {
  const navigate = useNavigate();
  const [aiResponse, setAiResponse] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Load AI response from localStorage
    const storedResponse = localStorage.getItem('golden_solution_response');
    
    if (storedResponse) {
      setAiResponse(storedResponse);
      setIsLoading(false);
    } else {
      // If no response found, redirect back to form
      navigate('/golden-solution');
    }
  }, [navigate]);

  const handleBackToForm = () => {
    // Navigate back to form - form data is already stored in localStorage
    // The form will load the stored data automatically
    navigate('/golden-solution');
  };

  const handleNewAnalysis = () => {
    // Clear all stored data
    localStorage.removeItem('golden_solution_form_data');
    localStorage.removeItem('golden_solution_response');
    
    // Navigate to form with clean state
    navigate('/golden-solution');
  };

  // Download TXT function
  const handleDownloadTXT = () => {
    const goldenSolutionText = aiResponse;
    const blob = new Blob([goldenSolutionText], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'solucao-de-ouro.txt';
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Download PDF function
  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    const goldenSolutionText = aiResponse;
    
    // Add title
    doc?.setFontSize(18);
    doc?.setFont(undefined, 'bold');
    doc?.text('Solução de Ouro', 20, 20);
    
    // Add content
    doc?.setFontSize(12);
    doc?.setFont(undefined, 'normal');
    
    // Split text into lines that fit the page width
    const pageWidth = doc?.internal?.pageSize?.getWidth();
    const margins = 20;
    const maxWidth = pageWidth - (margins * 2);
    
    const lines = doc?.splitTextToSize(goldenSolutionText, maxWidth);
    
    let yPosition = 35;
    const lineHeight = 7;
    const pageHeight = doc?.internal?.pageSize?.getHeight();
    
    lines?.forEach((line) => {
      if (yPosition + lineHeight > pageHeight - margins) {
        doc?.addPage();
        yPosition = 20;
      }
      doc?.text(line, margins, yPosition);
      yPosition += lineHeight;
    });
    
    doc?.save('solucao-de-ouro.pdf');
  };

  // Download DOCX function
  const handleDownloadDOCX = async () => {
    const goldenSolutionText = aiResponse;
    
    // Split text into paragraphs
    const paragraphs = goldenSolutionText?.split('\n')?.filter(p => p?.trim() !== '');
    
    const doc = new Document({
      sections: [
        {
          properties: {},
          children: [
            // Title
            new Paragraph({
              text: 'Solução de Ouro',
              heading: HeadingLevel.HEADING_1,
              spacing: {
                after: 400,
              },
            }),
            // Content paragraphs
            ...paragraphs.map(
              (para) =>
                new Paragraph({
                  children: [
                    new TextRun({
                      text: para,
                      size: 24,
                    }),
                  ],
                  spacing: {
                    after: 200,
                  },
                })
            ),
          ],
        },
      ],
    });
    
    const blob = await Packer?.toBlob(doc);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'solucao-de-ouro.docx';
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const isDownloadDisabled = !aiResponse || isLoading;

  return (
    <div className="min-h-screen bg-slate-50">
      <main className="p-4 lg:p-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <GlobalNavigationMenu />
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-full flex items-center justify-center">
                    <span className="text-lg">⭐</span>
                  </div>
                  <div>
                    <h1 className="text-3xl lg:text-4xl font-bold text-slate-800">
                      Resultado — Solução de Ouro
                    </h1>
                    <p className="text-slate-600 mt-1">
                      Sua análise personalizada está pronta
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Result Card */}
          <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6 lg:p-8">
            <h2 className="text-2xl font-semibold text-slate-800 mb-6">
              Sua Solução de Ouro
            </h2>

            {/* Result Content */}
            <div className="mb-6">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                    <p className="text-slate-600 font-medium">Gerando sua Solução de Ouro…</p>
                  </div>
                </div>
              ) : (
                <div className="prose prose-slate max-w-none">
                  <MarkdownRenderer content={aiResponse} />
                </div>
              )}
            </div>

            {/* Action Buttons */}
            {!isLoading && (
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={handleBackToForm}
                  variant="outline"
                  size="lg"
                  className="flex-1 border-2 border-slate-300 text-slate-700 hover:bg-slate-50 font-semibold py-3 rounded-lg transition-all duration-200"
                >
                  Voltar e Editar Formulário
                </Button>
                <Button
                  onClick={handleNewAnalysis}
                  variant="primary"
                  size="lg"
                  className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-3 rounded-lg shadow-md transition-all duration-200"
                >
                  Nova Análise
                </Button>
              </div>
            )}
          </div>

          {/* Download Section */}
          {!isLoading && (
            <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6 lg:p-8 mt-6">
              <h2 className="text-xl font-semibold text-slate-800 mb-4">
                Baixar Resposta
              </h2>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={handleDownloadPDF}
                  disabled={isDownloadDisabled}
                  variant="outline"
                  size="lg"
                  className="flex-1 border-2 border-red-500 text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed font-semibold py-3 rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
                >
                  <Download className="w-5 h-5" />
                  Baixar PDF
                </Button>
                <Button
                  onClick={handleDownloadDOCX}
                  disabled={isDownloadDisabled}
                  variant="outline"
                  size="lg"
                  className="flex-1 border-2 border-blue-500 text-blue-600 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed font-semibold py-3 rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
                >
                  <Download className="w-5 h-5" />
                  Baixar DOCX
                </Button>
                <Button
                  onClick={handleDownloadTXT}
                  disabled={isDownloadDisabled}
                  variant="outline"
                  size="lg"
                  className="flex-1 border-2 border-slate-500 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed font-semibold py-3 rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
                >
                  <Download className="w-5 h-5" />
                  Baixar TXT
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}